import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';
import './Event.css';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

const EventFormModal = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the event ID from the URL
  const [greetings, setGreetings] = useState('');
  const [questions, setQuestions] = useState([{ text: '' }]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [lastMessage, setLastMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Language states
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(true);

  const axiosConfig = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  };

  // Fetch languages from API
  const fetchLanguages = async () => {
    try {
      setLanguagesLoading(true);
      const response = await axios.get(`${API_URL}/api/languages?limit=100`, axiosConfig);
      const result = response.data;

      if (result.success) {
        setLanguages(result.data);
      } else {
        console.error('Failed to fetch languages:', result.message);
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setError('Failed to fetch languages. Using default options.');
      // Fallback to some basic languages if API fails
      setLanguages([
        { name: 'English' },
        { name: 'Spanish' },
        { name: 'Portuguese' },
        { name: 'French' },
        { name: 'German' }
      ]);
    } finally {
      setLanguagesLoading(false);
    }
  };

  // Fetch languages on component mount
  useEffect(() => {
    fetchLanguages();
  }, []);

  // Fetch event data when the component mounts
  useEffect(() => {
    if (id) {
      const fetchEvent = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`${API_URL}/auth/event/questions/${id}`);
          const event = response.data.event;
          setSelectedLanguage(event.language);
          setGreetings(event.greetings);
          setQuestions(event.questions);
          setLastMessage(event.lastMessage);
        } catch (err) {
          setError('Error fetching event data');
        } finally {
          setIsLoading(false);
        }
      };
      fetchEvent();
    }
  }, [id]);

  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].text = value;
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    setQuestions([...questions, { text: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const eventData = {
        language: selectedLanguage,
        greetings,
        questions,
        lastMessage,
      };

      if (id) {
        // Update existing event
        await axios.put(`${API_URL}/auth/event/questions/${id}`, eventData);
      } else {
        // Create new event
        await axios.post(`${API_URL}/auth/event/questions`, eventData);
      }

      // Redirect to the event list page
      navigate('/event');
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving event');
    } finally {
      setIsLoading(false);
    }
  };

  // Convert languages to react-select format
  const languageOptions = languages.map((language) => ({
    value: language.name,
    label: language.name,
  }));

  // Custom loading component for react-select
  const LoadingMessage = () => (
    <div style={{ padding: '10px', textAlign: 'center' }}>
      Loading languages...
    </div>
  );

  // Custom no options message
  const NoOptionsMessage = () => (
    <div style={{ padding: '10px', textAlign: 'center' }}>
      {languagesLoading ? 'Loading languages...' : 'No languages available'}
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h1 className="form-title">Setup Question</h1>
        </div>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="language">Language</label>
            <Select
              id="language"
              value={languageOptions.find(option => option.value === selectedLanguage)}
              onChange={(selectedOption) => setSelectedLanguage(selectedOption?.value || '')}
              options={languageOptions}
              isSearchable
              isLoading={languagesLoading}
              placeholder={languagesLoading ? "Loading languages..." : "Select a Language"}
              loadingMessage={LoadingMessage}
              noOptionsMessage={NoOptionsMessage}
              required
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#673AB7' : provided.borderColor,
                  boxShadow: state.isFocused ? '0 0 0 1px #673AB7' : provided.boxShadow,
                  '&:hover': {
                    borderColor: '#673AB7'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#673AB7' 
                    : state.isFocused 
                    ? 'rgba(103, 58, 183, 0.1)' 
                    : provided.backgroundColor,
                })
              }}
            />
            {languagesLoading && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '5px',
                fontStyle: 'italic'
              }}>
                Fetching available languages...
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="greetings">Greetings</label>
            <textarea
              id="greetings"
              type="text"
              rows="7"
              cols="90"
              value={greetings}
              onChange={(e) => setGreetings(e.target.value)}
              placeholder="Enter Greetings"
              required
              style={{
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                fontSize: "15px",
                width: "100%",
                resize: "vertical",
              }}
            ></textarea>
          </div>

          <div className="form-group">
            <label>Questions</label>
            {questions.map((question, index) => (
              <div key={index} className="question-field">
                <input
                  type="text"
                  value={question.text}
                  onChange={(e) => handleQuestionChange(index, e.target.value)}
                  placeholder={`Enter Question ${index + 1}`}
                  required
                />
                {index === questions.length - 1 && (
                  <button type="button" className="add-question" onClick={addQuestion}>
                    +
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="form-group">
            <label htmlFor="lastMessage">Last Message</label>
            <textarea
              id="lastMessage"
              type="text"
              rows="7"
              cols="90"
              value={lastMessage}
              onChange={(e) => setLastMessage(e.target.value)}
              placeholder="Enter Last Message"
              required
              style={{
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                fontSize: "15px",
                width: "100%",
                resize: "vertical",
              }}
            ></textarea>
          </div>

          <div className='form-buttons'>
            <button type="button" onClick={() => navigate('/event')} className="cancel-button">
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isLoading || languagesLoading} 
              className="submit-button"
            >
              {isLoading ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update Flow' : 'Create Flow')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventFormModal;
